import { debug } from "./debug";
import {
  DEFAULT_MAX_TOKENS,
  LLM_TEMPERATURE,
  LLM_TOP_P,
  THINKING_TOKEN_MULTIPLIER,
  MAX_TOKEN_CEILING,
} from "./config";
import { TruncationError } from "./errors";
import { extractJSON, extractAnthropicText } from "./extract";
import { extractUsage } from "./usage";
import { cloudRequest, geminiRoute } from "./cloudRoute";
import { WORKOUT_PLAN_SCHEMA } from "./schemas";
import { openAIRequestLimits, anthropicMaxTokens } from "./requestLimits";
import { buildOpenRouterReasoning, readReasoningDelta } from "./reasoning";
import {
  generateGeminiText,
  generateGeminiTextStream,
  supportsGeminiThinking,
} from "../geminiApi";

// --- PROVIDER RESPONSE HELPERS ---

// Hand the caller this call's token/cost figures (issue #71). Reported even on
// a truncated response — those tokens were still billed, so a generation that
// retried its way to an answer must own the cost of the attempts that failed.
const reportUsage = (onUsage, provider, model, payload) => {
  if (typeof onUsage !== "function") return;
  const entry = extractUsage(provider, model, payload);
  if (entry) onUsage(entry);
};

const logRequest = (provider, url, body) => {
  debug.group(`${provider} Request`);
  debug.log("URL:", url);
  debug.log("Model:", body?.model);
  debug.log("Request body:", JSON.stringify(body, null, 2).substring(0, 1000));
  debug.time(`${provider} response`);
  debug.groupEnd();
};

const logResponse = (provider, response, data) => {
  debug.timeEnd(`${provider} response`);
  debug.group(`${provider} Response`);
  const headers = response?.headers?.entries
    ? Object.fromEntries([...response.headers.entries()])
    : {};
  const dataKeys = data && typeof data === "object" ? Object.keys(data) : [];

  debug.log("Status:", response?.status, response?.statusText);
  debug.log("Headers:", headers);
  debug.log(
    "Response body (first 1000 chars):",
    JSON.stringify(data).substring(0, 1000),
  );
  debug.log("Response structure keys:", dataKeys);
  if (data?.choices)
    debug.log("choices[0] keys:", Object.keys(data.choices[0] || {}));
  if (data?.content)
    debug.log(
      "content type:",
      typeof data.content,
      Array.isArray(data.content) ? `[${data.content.length} items]` : "",
    );
  if (data?.candidates)
    debug.log("candidates[0] keys:", Object.keys(data.candidates[0] || {}));
  if (data?.usage) debug.table(data.usage);
  debug.groupEnd();
};

const logError = (provider, response, errorBody) => {
  debug.group(`${provider} Error`);
  debug.error("Status:", response.status, response.statusText);
  debug.error("Error body:", JSON.stringify(errorBody, null, 2));
  debug.groupEnd();
};

// --- PROVIDER IMPLEMENTATIONS ---
// All cloud providers route through /proxy/* to avoid CORS issues.
// The Express server proxies these to the actual API endpoints. On a mate's
// shared credits cloudRequest sends them to the API instead (cloudRoute.js).

const _generateWithOllama = async (
  prompt,
  settings,
  signal,
  maxTokens = DEFAULT_MAX_TOKENS,
  onUsage = null,
) => {
  const url = "/ollama-api/generate";
  const body = {
    model: settings.model,
    prompt: prompt,
    stream: false,
    format: "json",
    options: {
      temperature: LLM_TEMPERATURE,
      top_p: LLM_TOP_P,
      num_predict: maxTokens,
    },
  };
  logRequest("Ollama", url, body);

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    logError("Ollama", response, errorBody);
    throw new Error(`Ollama API Error: ${response.status} - ${errorBody}`);
  }

  const data = await response.json();
  logResponse("Ollama", response, {
    ...data,
    response: data.response?.substring(0, 500) + "...",
  });
  reportUsage(onUsage, "ollama", settings.model, data);

  // Ollama signals incomplete generation with done=false
  if (data.done === false) {
    throw new TruncationError(
      `Ollama response incomplete (done=false) at ${maxTokens} tokens`,
      { rawText: data.response, provider: "ollama" },
    );
  }

  return extractJSON(data.response);
};

const _generateWithOpenAI = async (
  prompt,
  settings,
  signal,
  maxTokens = DEFAULT_MAX_TOKENS,
  onUsage = null,
) => {
  const route = cloudRequest(settings, "/openai/chat/completions", {
    "Content-Type": "application/json",
    Authorization: `Bearer ${settings.apiKey}`,
  });
  const url = route.url;
  // Reasoning headroom, `max_completion_tokens`, no temperature (#165).
  const limits = openAIRequestLimits("openai", maxTokens);
  const body = {
    model: settings.model,
    messages: [
      {
        role: "system",
        content:
          "You are a fitness coach. Always respond with valid JSON only.",
      },
      { role: "user", content: prompt },
    ],
    response_format: { type: "json_object" },
    ...limits,
  };
  logRequest("OpenAI", url, body);

  const response = await fetch(url, {
    method: "POST",
    headers: route.headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.json();
    logError("OpenAI", response, errorBody);
    throw new Error(
      `OpenAI API Error: ${errorBody.error?.message || response.status}`,
    );
  }

  const data = await response.json();
  logResponse("OpenAI", response, data);
  reportUsage(onUsage, "openai", settings.model, data);

  if (data.choices?.[0]?.finish_reason === "length") {
    throw new TruncationError(
      `OpenAI response truncated at ${limits.max_completion_tokens} tokens`,
      { rawText: data.choices[0].message.content, provider: "openai" },
    );
  }

  return extractJSON(data.choices[0].message.content);
};

const _generateWithAnthropic = async (
  prompt,
  settings,
  signal,
  maxTokens = DEFAULT_MAX_TOKENS,
  onUsage = null,
) => {
  const route = cloudRequest(settings, "/anthropic/messages", {
    "Content-Type": "application/json",
    "x-api-key": settings.apiKey,
    "anthropic-version": "2023-06-01",
  });
  const url = route.url;
  // Thinking headroom and no temperature: see requestLimits.js (#165).
  const effectiveTokens = anthropicMaxTokens("anthropic", maxTokens);
  const body = {
    model: settings.model,
    max_tokens: effectiveTokens,
    system:
      "You are a fitness coach. Always respond with valid JSON only, no explanations.",
    messages: [
      {
        role: "user",
        content:
          prompt +
          "\n\nIMPORTANT: Respond with ONLY the JSON object, no other text.",
      },
    ],
  };
  logRequest("Anthropic", url, body);

  const response = await fetch(url, {
    method: "POST",
    headers: route.headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.json();
    logError("Anthropic", response, errorBody);
    throw new Error(
      `Anthropic API Error: ${errorBody.error?.message || response.status}`,
    );
  }

  const data = await response.json();
  logResponse("Anthropic", response, data);
  reportUsage(onUsage, "anthropic", settings.model, data);

  if (data.stop_reason === "max_tokens") {
    throw new TruncationError(
      `Anthropic response truncated at ${effectiveTokens} tokens`,
      { rawText: extractAnthropicText(data), provider: "anthropic" },
    );
  }

  return extractJSON(extractAnthropicText(data));
};

const _generateWithGoogle = async (
  prompt,
  settings,
  signal,
  onThinking,
  maxTokens = DEFAULT_MAX_TOKENS,
  onUsage = null,
) => {
  const thinkingLevel = settings.geminiThinkingLevel || "HIGH";
  const modelSupportsThinking = supportsGeminiThinking(settings.model);
  const isThinkingActive = modelSupportsThinking && thinkingLevel !== "OFF";

  // Thinking tokens count against maxOutputTokens, so a thinking model needs the
  // plan's budget PLUS room to reason. MULTIPLY the caller's figure rather than
  // flooring it: `Math.max(maxTokens, DEFAULT_MAX_TOKENS * 4)` pinned every
  // truncation retry to the same 32768, so generateWorkoutPlan's escalation
  // (8192 → 16384 → 32768) re-sent an identical request three times and
  // truncated identically each time. The first attempt is unchanged at 8192 × 4.
  const effectiveTokens = isThinkingActive
    ? Math.min(maxTokens * THINKING_TOKEN_MULTIPLIER, MAX_TOKEN_CEILING)
    : maxTokens;

  const config = {
    maxOutputTokens: effectiveTokens,
    responseMimeType: "application/json",
    // `responseJsonSchema` takes STANDARD JSON Schema (lowercase `type`,
    // `anyOf`, `required`) — it is the alternative to `responseSchema`, which is
    // the uppercase OpenAPI dialect `toGeminiSchema` produces. This used to send
    // the converted schema, i.e. the OpenAPI dialect into the JSON Schema field;
    // the schema was at best tolerated and at worst ignored, which is one way a
    // structurally invalid plan reached the post-processor.
    // A caller whose output is not a bare plan (a described workout carries a
    // `meta` object too) passes its own schema, or the model drops what the
    // plan schema doesn't name.
    responseJsonSchema: settings.responseSchema || WORKOUT_PLAN_SCHEMA,
    abortSignal: signal,
  };

  if (isThinkingActive) {
    config.thinkingConfig = {
      thinkingLevel,
      includeThoughts: true,
    };
  }

  const contents = prompt + "\n\nRespond with valid JSON only.";

  logRequest("Google", settings.model, {
    model: settings.model,
    contents,
    config,
  });

  let text;

  // The SDK hands back usageMetadata rather than an HTTP body we can inspect.
  const onGeminiUsage = (usageMetadata) =>
    reportUsage(onUsage, "google", settings.model, { usageMetadata });

  debug.log("Google thinking:", {
    thinkingLevel,
    modelSupportsThinking,
    isThinkingActive,
    hasOnThinking: !!onThinking,
    willStream: isThinkingActive && !!onThinking,
  });

  if (isThinkingActive && onThinking) {
    // Stream to get live thinking updates in the UI
    text = await generateGeminiTextStream({
      ...geminiRoute(settings, settings.apiKey),
      model: settings.model,
      contents,
      config,
      onThinking,
      onUsage: onGeminiUsage,
    });
  } else {
    text = await generateGeminiText({
      ...geminiRoute(settings, settings.apiKey),
      model: settings.model,
      contents,
      config,
      onUsage: onGeminiUsage,
    });
  }

  logResponse("Google", { status: 200, statusText: "SDK" }, { text });
  return extractJSON(text);
};

// --- ANTHROPIC-COMPATIBLE SSE STREAM PARSER ---
// Parses Server-Sent Events from Anthropic-style streaming responses.
// Handles thinking deltas, text content, and tool_use (input_json_delta) blocks.
// Returns `{ text, usage }` — text is the tool input JSON if tool_use was
// invoked, otherwise the free-form response. Usage arrives split across two
// events: input tokens on message_start, output tokens on message_delta.
const _parseAnthropicStream = async (response, signal, onThinking) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let thinkingText = "";
  let responseText = "";
  let toolInputJson = "";
  let currentBlockType = null;
  const usage = {};

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (signal?.aborted) {
        reader.cancel();
        throw new DOMException("Aborted", "AbortError");
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || ""; // Keep incomplete line in buffer

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6).trim();
        if (data === "[DONE]") continue;

        let event;
        try {
          event = JSON.parse(data);
        } catch {
          continue;
        }

        switch (event.type) {
          case "message_start":
            // Input tokens are only ever reported here.
            Object.assign(usage, event.message?.usage || {});
            break;

          case "content_block_start":
            currentBlockType = event.content_block?.type || null;
            debug.log("Stream: block start →", currentBlockType);
            if (currentBlockType === "thinking" && onThinking) {
              onThinking(""); // Signal thinking started
            }
            break;

          case "content_block_delta":
            if (
              event.delta?.type === "thinking_delta" &&
              event.delta.thinking
            ) {
              thinkingText += event.delta.thinking;
              if (onThinking) onThinking(thinkingText);
            } else if (event.delta?.type === "text_delta" && event.delta.text) {
              responseText += event.delta.text;
            } else if (
              event.delta?.type === "input_json_delta" &&
              event.delta.partial_json
            ) {
              toolInputJson += event.delta.partial_json;
            }
            break;

          case "content_block_stop":
            debug.log("Stream: block stop, type was:", currentBlockType);
            currentBlockType = null;
            break;

          case "message_delta":
            debug.log(
              "Stream: message_delta, stop_reason:",
              event.delta?.stop_reason,
            );
            if (event.usage) {
              debug.table(event.usage);
              // Cumulative output count; the last one wins.
              Object.assign(usage, event.usage);
            }
            break;

          case "error":
            throw new Error(
              `Stream error: ${event.error?.message || JSON.stringify(event)}`,
            );

          default:
            // Other event types (message_start/stop, ping, …) need no action.
            break;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  debug.log(
    "Stream complete. Thinking length:",
    thinkingText.length,
    "Text length:",
    responseText.length,
    "Tool JSON length:",
    toolInputJson.length,
  );
  // Prefer tool input JSON (structured output) over free-form text
  return { text: toolInputJson || responseText, usage };
};

const _generateWithMiniMax = async (
  prompt,
  settings,
  signal,
  onThinking,
  maxTokens = DEFAULT_MAX_TOKENS,
  onUsage = null,
) => {
  // MiniMax uses Anthropic Messages API format with streaming for live thinking.
  // Tool calling forces structured JSON output matching WORKOUT_PLAN_SCHEMA.
  const route = cloudRequest(settings, "/minimax/v1/messages", {
    "Content-Type": "application/json",
    "x-api-key": settings.apiKey,
    "anthropic-version": "2023-06-01",
  });
  const url = route.url;
  const body = {
    model: settings.model,
    max_tokens: maxTokens,
    stream: true,
    temperature: LLM_TEMPERATURE,
    system:
      "You are a fitness coach. Generate a structured workout plan using the provided tool.",
    messages: [
      {
        role: "user",
        content: prompt,
      },
    ],
    tools: [
      {
        name: "generate_workout_plan",
        description:
          "Generate a structured workout plan. The output must be a valid workout_plan object.",
        input_schema: settings.responseSchema || WORKOUT_PLAN_SCHEMA,
      },
    ],
    tool_choice: { type: "tool", name: "generate_workout_plan" },
  };
  logRequest("MiniMax", url, body);

  const response = await fetch(url, {
    method: "POST",
    headers: route.headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text();
    logError("MiniMax", response, errorBody);
    let msg;
    try {
      msg = JSON.parse(errorBody).error?.message;
    } catch {}
    throw new Error(`MiniMax API Error: ${msg || response.status}`);
  }

  debug.time("MiniMax stream");
  const { text, usage } = await _parseAnthropicStream(
    response,
    signal,
    onThinking,
  );
  debug.timeEnd("MiniMax stream");
  debug.log("Streamed text (first 500 chars):", text.substring(0, 500));
  reportUsage(onUsage, "minimax", settings.model, { usage });

  return extractJSON(text);
};

const _generateWithDeepSeek = async (
  prompt,
  settings,
  signal,
  maxTokens = DEFAULT_MAX_TOKENS,
  onUsage = null,
) => {
  const route = cloudRequest(settings, "/deepseek/chat/completions", {
    "Content-Type": "application/json",
    Authorization: `Bearer ${settings.apiKey}`,
  });
  const url = route.url;
  const body = {
    model: settings.model,
    messages: [
      {
        role: "system",
        content:
          "You are a fitness coach. Always respond with valid JSON only.",
      },
      { role: "user", content: prompt },
    ],
    response_format: { type: "json_object" },
    temperature: LLM_TEMPERATURE,
    max_tokens: maxTokens,
  };
  logRequest("DeepSeek", url, body);

  const response = await fetch(url, {
    method: "POST",
    headers: route.headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.json();
    logError("DeepSeek", response, errorBody);
    throw new Error(
      `DeepSeek API Error: ${errorBody.error?.message || response.status}`,
    );
  }

  const data = await response.json();
  logResponse("DeepSeek", response, data);
  reportUsage(onUsage, "deepseek", settings.model, data);

  if (data.choices?.[0]?.finish_reason === "length") {
    throw new TruncationError(
      `DeepSeek response truncated at ${maxTokens} tokens`,
      { rawText: data.choices[0].message.content, provider: "deepseek" },
    );
  }

  return extractJSON(data.choices[0].message.content);
};

const _generateWithGroq = async (
  prompt,
  settings,
  signal,
  maxTokens = DEFAULT_MAX_TOKENS,
  onUsage = null,
) => {
  const route = cloudRequest(settings, "/groq/chat/completions", {
    "Content-Type": "application/json",
    Authorization: `Bearer ${settings.apiKey}`,
  });
  const url = route.url;
  const body = {
    model: settings.model,
    messages: [
      {
        role: "system",
        content:
          "You are a fitness coach. Always respond with valid JSON only.",
      },
      { role: "user", content: prompt },
    ],
    response_format: { type: "json_object" },
    temperature: LLM_TEMPERATURE,
    max_tokens: maxTokens,
  };
  logRequest("Groq", url, body);

  const response = await fetch(url, {
    method: "POST",
    headers: route.headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.json();
    logError("Groq", response, errorBody);
    throw new Error(
      `Groq API Error: ${errorBody.error?.message || response.status}`,
    );
  }

  const data = await response.json();
  logResponse("Groq", response, data);
  reportUsage(onUsage, "groq", settings.model, data);

  if (data.choices?.[0]?.finish_reason === "length") {
    throw new TruncationError(
      `Groq response truncated at ${maxTokens} tokens`,
      { rawText: data.choices[0].message.content, provider: "groq" },
    );
  }

  return extractJSON(data.choices[0].message.content);
};

// --- OPENAI-DIALECT SSE STREAM PARSER ---
// OpenRouter speaks OpenAI's streaming format for every upstream vendor, so
// one parser covers the catalogue. Reasoning arrives on `delta.reasoning`
// interleaved with the answer on `delta.content`; usage arrives once, on the
// final chunk, and only because we asked for `stream_options.include_usage`.
const _parseOpenAIStream = async (response, signal, onThinking) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let thinkingText = "";
  let responseText = "";
  let finishReason = null;
  let usage = null;

  const handleEvent = (event) => {
    // Upstream failures arrive mid-stream rather than as an HTTP error.
    if (event.error) {
      throw new Error(
        `OpenRouter API Error: ${event.error.message || "stream failed"}`,
      );
    }

    if (event.usage) usage = event.usage;

    const choice = event.choices?.[0];
    if (!choice) return;
    if (choice.finish_reason) finishReason = choice.finish_reason;

    const delta = choice.delta || {};
    const reasoning = readReasoningDelta(delta);
    if (reasoning) {
      thinkingText += reasoning;
      if (onThinking) onThinking(thinkingText);
    }
    if (typeof delta.content === "string") responseText += delta.content;
  };

  try {
    for (;;) {
      if (signal?.aborted) {
        throw new DOMException("Generation aborted", "AbortError");
      }

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      // The last fragment may be half an event; keep it for the next chunk.
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmed = line.trim();
        // OpenRouter emits `: OPENROUTER PROCESSING` keep-alive comments while
        // an upstream model warms up. They are not events.
        if (!trimmed || trimmed.startsWith(":")) continue;
        if (!trimmed.startsWith("data:")) continue;

        const payload = trimmed.slice(5).trim();
        if (payload === "[DONE]") continue;

        let event;
        try {
          event = JSON.parse(payload);
        } catch {
          continue; // A truncated frame; the next chunk completes it.
        }

        handleEvent(event);
      }
    }
  } finally {
    reader.releaseLock?.();
  }

  debug.log("OpenRouter stream:", {
    thinkingChars: thinkingText.length,
    textChars: responseText.length,
    finishReason,
  });

  return { text: responseText, usage, finishReason };
};

const _generateWithOpenRouter = async (
  prompt,
  settings,
  signal,
  onThinking = null,
  maxTokens = DEFAULT_MAX_TOKENS,
  onUsage = null,
) => {
  // OpenRouter speaks the OpenAI chat-completions dialect for every upstream
  // vendor, so one implementation covers the whole catalogue.
  const route = cloudRequest(settings, "/openrouter/chat/completions", {
    "Content-Type": "application/json",
    Authorization: `Bearer ${settings.apiKey}`,
    // Attribution headers OpenRouter shows on its activity dashboard.
    "X-Title": "Khronos AI",
  });
  const url = route.url;

  // Reasoning is opt-in here, unlike the Google provider where the SDK returns
  // thoughts once `includeThoughts` is set: no `reasoning` field means no
  // trace, which is why a Gemini model routed through OpenRouter used to show
  // an empty thinking panel.
  const reasoning = buildOpenRouterReasoning(
    settings.model,
    settings.geminiThinkingLevel,
  );
  // Reasoning tokens bill against max_tokens just as Gemini's thinking tokens
  // do, so give them the same headroom rather than truncating mid-plan — and,
  // as there, multiply the caller's budget instead of flooring it so a
  // truncation retry actually asks for more than the attempt that truncated.
  const effectiveTokens = reasoning
    ? Math.min(maxTokens * THINKING_TOKEN_MULTIPLIER, MAX_TOKEN_CEILING)
    : maxTokens;
  // Only stream when there is somewhere to put the trace. The plain request is
  // simpler and surfaces OpenRouter's error envelope more directly.
  const shouldStream = Boolean(reasoning && onThinking);

  const body = {
    model: settings.model,
    messages: [
      {
        role: "system",
        content:
          "You are a fitness coach. Always respond with valid JSON only.",
      },
      { role: "user", content: prompt },
    ],
    response_format: { type: "json_object" },
    temperature: LLM_TEMPERATURE,
    max_tokens: effectiveTokens,
    ...(reasoning ? { reasoning } : {}),
    // Streamed responses omit usage unless asked, and usage is where
    // OpenRouter's real per-request cost lives.
    ...(shouldStream
      ? { stream: true, stream_options: { include_usage: true } }
      : {}),
  };
  logRequest("OpenRouter", url, body);

  const response = await fetch(url, {
    method: "POST",
    headers: route.headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    logError("OpenRouter", response, errorBody);
    throw new Error(
      `OpenRouter API Error: ${errorBody.error?.message || response.status}`,
    );
  }

  if (shouldStream && response.body) {
    debug.time("OpenRouter stream");
    const { text, usage, finishReason } = await _parseOpenAIStream(
      response,
      signal,
      onThinking,
    );
    debug.timeEnd("OpenRouter stream");

    // Reported before the truncation check: those tokens were still billed.
    reportUsage(onUsage, "openrouter", settings.model, { usage });

    if (finishReason === "length") {
      throw new TruncationError(
        `OpenRouter response truncated at ${effectiveTokens} tokens`,
        { rawText: text, provider: "openrouter" },
      );
    }

    return extractJSON(text);
  }

  const data = await response.json();
  logResponse("OpenRouter", response, data);

  // OpenRouter reports upstream failures as a 200 with an error envelope.
  if (data.error) {
    throw new Error(`OpenRouter API Error: ${data.error.message}`);
  }

  // The only provider here that returns real money: usage.cost is what this
  // request actually charged, in USD, no opt-in flag required.
  reportUsage(onUsage, "openrouter", settings.model, data);

  const message = data.choices?.[0]?.message;

  // A non-streamed reasoning response still carries the trace, just all at
  // once. Late is better than never — the panel would otherwise stay empty.
  if (onThinking) {
    const trace = readReasoningDelta(message);
    if (trace) onThinking(trace);
  }

  if (data.choices?.[0]?.finish_reason === "length") {
    throw new TruncationError(
      `OpenRouter response truncated at ${effectiveTokens} tokens`,
      { rawText: message?.content, provider: "openrouter" },
    );
  }

  return extractJSON(message.content);
};

// --- PROVIDER DISPATCH ---
// Centralised dispatch avoids duplicating the provider switch in every caller.

const _dispatchToProvider = async (
  prompt,
  settings,
  signal,
  maxTokens = DEFAULT_MAX_TOKENS,
  onThinking = null,
  onUsage = null,
) => {
  switch (settings.provider) {
    case "ollama":
      return _generateWithOllama(prompt, settings, signal, maxTokens, onUsage);
    case "openai":
      return _generateWithOpenAI(prompt, settings, signal, maxTokens, onUsage);
    case "anthropic":
      return _generateWithAnthropic(
        prompt,
        settings,
        signal,
        maxTokens,
        onUsage,
      );
    case "google":
      return _generateWithGoogle(
        prompt,
        settings,
        signal,
        onThinking,
        maxTokens,
        onUsage,
      );
    case "minimax":
      return _generateWithMiniMax(
        prompt,
        settings,
        signal,
        onThinking,
        maxTokens,
        onUsage,
      );
    case "deepseek":
      return _generateWithDeepSeek(
        prompt,
        settings,
        signal,
        maxTokens,
        onUsage,
      );
    case "groq":
      return _generateWithGroq(prompt, settings, signal, maxTokens, onUsage);
    case "openrouter":
      return _generateWithOpenRouter(
        prompt,
        settings,
        signal,
        onThinking,
        maxTokens,
        onUsage,
      );
    default:
      throw new Error(`Unsupported provider: ${settings.provider}`);
  }
};

export { _dispatchToProvider };
